import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { Space, Button, Select, Typography, message } from "@mimber/ui-web"
import { Form, SelectField } from "@mimber/forms"
import {
  useTaxRates,
  useUpdateTaxRateAssignments,
} from "@mimber/client/mimber-organization"
import type { UpdateOrganizationTaxRateAssignmentsDto } from "@mimber/schema"
import type { TaxRateAssignmentsFormProps } from "./TaxRateAssignmentsForm.types"
import {
  NO_TAX_VALUE,
  TAXABLE_SOURCE_TYPE_LABELS,
  TAXABLE_SOURCE_TYPE_ORDER,
  buildAssignmentsFormDefaults,
  type TaxRateAssignmentsFormData,
} from "./TaxRateAssignmentsForm.utils"

const { Paragraph } = Typography

export const TaxRateAssignmentsForm: React.FunctionComponent<
  TaxRateAssignmentsFormProps
> = ({ assignments, onSuccess }) => {
  const { data: taxRatesResponse } = useTaxRates()
  const activeTaxRates = (taxRatesResponse?.data || []).filter(
    (rate) => rate.isActive
  )

  const rateOptions = [
    { label: "No tax", value: NO_TAX_VALUE },
    ...activeTaxRates.map((rate) => ({
      label: `${rate.name} (${rate.ratePercentage}%)`,
      value: rate.uuid,
    })),
  ]

  const form = useForm<TaxRateAssignmentsFormData>({
    mode: "onBlur",
    defaultValues: buildAssignmentsFormDefaults(assignments),
  })

  const { setValue } = form

  useEffect(() => {
    form.reset(buildAssignmentsFormDefaults(assignments))
  }, [assignments, form])

  const mutation = useUpdateTaxRateAssignments({
    onSuccess: () => {
      message.success("Tax assignments saved")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to save tax assignments: ${error.message}`)
    },
  })

  const handleApplyToAll = (taxRateId: string) => {
    for (const sourceType of TAXABLE_SOURCE_TYPE_ORDER) {
      setValue(sourceType, taxRateId, { shouldDirty: true })
    }
  }

  const onSubmit = (data: TaxRateAssignmentsFormData) => {
    const dto: UpdateOrganizationTaxRateAssignmentsDto = {
      assignments: TAXABLE_SOURCE_TYPE_ORDER.map((sourceType) => ({
        sourceType,
        taxRateId: data[sourceType] === NO_TAX_VALUE ? null : data[sourceType],
      })),
    }
    mutation.mutate(dto)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <Paragraph type="secondary">
        Choose which tax rate (if any) applies to each type of revenue. Leave a
        type set to "No tax" to keep it exempt.
      </Paragraph>

      {activeTaxRates.length > 0 && (
        <Space className="mb-6">
          <span>Apply one rate to all types:</span>
          <Select
            className="min-w-55"
            placeholder="Select a rate"
            options={activeTaxRates.map((rate) => ({
              label: `${rate.name} (${rate.ratePercentage}%)`,
              value: rate.uuid,
            }))}
            onChange={handleApplyToAll}
          />
        </Space>
      )}

      {TAXABLE_SOURCE_TYPE_ORDER.map((sourceType) => (
        <SelectField
          key={sourceType}
          name={sourceType}
          label={TAXABLE_SOURCE_TYPE_LABELS[sourceType]}
          options={rateOptions}
        />
      ))}

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Save Assignments
        </Button>
      </Space>
    </Form>
  )
}
